import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import InstallPWA from "./components/InstallPWA";
import InAppHelpWidget from "./components/InAppHelpWidget";

// Eager-load only the first paint surfaces (landing + auth) to keep first-byte fast.
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";

// Lazy-load every other page → smaller initial bundle, faster cold start.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Contacts = lazy(() => import("./pages/Contacts"));
const ContactForm = lazy(() => import("./pages/ContactForm"));
const Quotes = lazy(() => import("./pages/Quotes"));
const QuoteForm = lazy(() => import("./pages/QuoteForm"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceForm = lazy(() => import("./pages/InvoiceForm"));
const Settings = lazy(() => import("./pages/Settings"));
const PriceLibrary = lazy(() => import("./pages/PriceLibrary"));
const Accounting = lazy(() => import("./pages/Accounting"));
const Pricing = lazy(() => import("./pages/Pricing"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const KboAdmin = lazy(() => import("./pages/KboAdmin"));
const DrivingMode = lazy(() => import("./pages/DrivingMode"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const Activity = lazy(() => import("./pages/Activity"));
const Employees = lazy(() => import("./pages/Employees"));
const EmployeeForm = lazy(() => import("./pages/EmployeeForm"));
const Leaves = lazy(() => import("./pages/Leaves"));
const Calendar = lazy(() => import("./pages/Calendar"));
const BookingPublic = lazy(() => import("./pages/BookingPublic"));
const BookingSettings = lazy(() => import("./pages/BookingSettings"));
const Stock = lazy(() => import("./pages/Stock"));
const Team = lazy(() => import("./pages/Team"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const OnboardingInviteTeam = lazy(() => import("./pages/OnboardingInviteTeam"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminOrganizations = lazy(() => import("./pages/AdminOrganizations"));
const MonthlyBilling = lazy(() => import("./pages/MonthlyBilling"));
const Meetings = lazy(() => import("./pages/Meetings"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Tasks = lazy(() => import("./pages/Tasks"));
const TasksReport = lazy(() => import("./pages/TasksReport"));
const TripsAudit = lazy(() => import("./pages/TripsAudit"));
const PublicQuote = lazy(() => import("./pages/PublicQuote"));
const PublicAppointment = lazy(() => import("./pages/PublicAppointment"));
const Invitations = lazy(() => import("./pages/Invitations"));
const EmailLogs = lazy(() => import("./pages/EmailLogs"));

// Shared loading fallback shown while a lazy chunk downloads (typically <300ms).
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
  </div>
);

const FullPageLoader = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

// Protected Route Component without Layout (for full-page experiences)
const ProtectedRouteFullPage = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />

        {/* Auth Routes */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

        {/* Public (no auth) */}
        <Route path="/book/:slug" element={<BookingPublic />} />
        <Route path="/q/:token" element={<PublicQuote />} />
        <Route path="/a/:token" element={<PublicAppointment />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        {/* CRM */}
        <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
        <Route path="/contacts/new" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />
        <Route path="/contacts/:id" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />
        <Route path="/contacts/:id/edit" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />

        {/* Quotes */}
        <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
        <Route path="/quotes/new" element={<ProtectedRoute><QuoteForm /></ProtectedRoute>} />
        <Route path="/quotes/:id" element={<ProtectedRoute><QuoteForm /></ProtectedRoute>} />

        {/* Invoices */}
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/invoices/monthly" element={<ProtectedRoute><MonthlyBilling /></ProtectedRoute>} />
        <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
        <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
        <Route path="/invoices/new" element={<ProtectedRoute><InvoiceForm /></ProtectedRoute>} />
        <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceForm /></ProtectedRoute>} />

        {/* Settings */}
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

        {/* Price Library / Misc */}
        <Route path="/price-library" element={<ProtectedRoute><PriceLibrary /></ProtectedRoute>} />
        <Route path="/accounting" element={<ProtectedRoute><Accounting /></ProtectedRoute>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/kbo" element={<ProtectedRoute><KboAdmin /></ProtectedRoute>} />
        <Route path="/driving" element={<ProtectedRouteFullPage><DrivingMode /></ProtectedRouteFullPage>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />

        {/* Activity */}
        <Route path="/activity" element={<ProtectedRoute><Activity /></ProtectedRoute>} />

        {/* Calendar */}
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
        <Route path="/invitations" element={<ProtectedRoute><Invitations /></ProtectedRoute>} />
        <Route path="/email-logs" element={<ProtectedRoute><EmailLogs /></ProtectedRoute>} />
        <Route path="/booking-settings" element={<ProtectedRoute><BookingSettings /></ProtectedRoute>} />
        <Route path="/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/organizations" element={<ProtectedRoute><AdminOrganizations /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
        <Route path="/tasks/report" element={<ProtectedRoute><TasksReport /></ProtectedRoute>} />
        <Route path="/trips/audit" element={<ProtectedRoute><TripsAudit /></ProtectedRoute>} />
        <Route path="/onboarding/invite-team" element={<ProtectedRouteFullPage><OnboardingInviteTeam /></ProtectedRouteFullPage>} />

        {/* HR */}
        <Route path="/hr/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
        <Route path="/hr/employees/new" element={<ProtectedRoute><EmployeeForm /></ProtectedRoute>} />
        <Route path="/hr/employees/:id" element={<ProtectedRoute><EmployeeForm /></ProtectedRoute>} />
        <Route path="/hr/leaves" element={<ProtectedRoute><Leaves /></ProtectedRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <InstallPWA />
        <InAppHelpWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
